import * as React from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {API_BASE_URL} from "../../../../config/apiConfig";

export interface BranchFormErrors {
    code?: string;
    name?: string;
    city?: string;
    region?: string;
}

export interface AddBranchDialogProps {
    open: boolean;
    token: string | null;
    newCode: string;
    setNewCode: (value: string) => void;
    newName: string;
    setNewName: (value: string) => void;
    newCity: string;
    setNewCity: (value: string) => void;
    newRegion: string;
    setNewRegion: (value: string) => void;
    newIsActive: boolean;
    setNewIsActive: (value: boolean) => void;
    formErrors: BranchFormErrors;
    setFormErrors: React.Dispatch<React.SetStateAction<BranchFormErrors>>;
    validateForm: () => boolean;
    setError: (message: string | null) => void;
    setSuccessMessageText: (text: string) => void;
    setShowSuccessMessage: (open: boolean) => void;
    fetchBranches: () => Promise<void>;
    onClose: () => void;
}

export default function AddBranchDialog({
    open,
    token,
    newCode,
    setNewCode,
    newName,
    setNewName,
    newCity,
    setNewCity,
    newRegion,
    setNewRegion,
    newIsActive,
    setNewIsActive,
    formErrors,
    setFormErrors,
    validateForm,
    setError,
    setSuccessMessageText,
    setShowSuccessMessage,
    fetchBranches,
    onClose,
}: Readonly<AddBranchDialogProps>): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === "backdropClick") return; // block outside click
                onClose();
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{t('hr.branches.dialog.add.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                    <TextField
                        label={t('hr.branches.table.header.code')}
                        fullWidth
                        size="small"
                        required
                        value={newCode}
                        onChange={(e) => {
                            setNewCode(e.target.value);
                            setFormErrors((p) => ({...p, code: undefined}));
                        }}
                        error={!!formErrors.code}
                        helperText={formErrors.code}
                    />
                    <TextField
                        label={t('hr.branches.table.header.name')}
                        fullWidth
                        size="small"
                        required
                        value={newName}
                        onChange={(e) => {
                            setNewName(e.target.value);
                            setFormErrors((p) => ({...p, name: undefined}));
                        }}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                    />
                    <TextField
                        label={t('hr.branches.table.header.city')}
                        fullWidth
                        size="small"
                        required
                        value={newCity}
                        onChange={(e) => {
                            setNewCity(e.target.value);
                            setFormErrors((p) => ({...p, city: undefined}));
                        }}
                        error={!!formErrors.city}
                        helperText={formErrors.city}
                    />
                    <TextField
                        label={t('hr.branches.table.header.region')}
                        fullWidth
                        size="small"
                        required
                        value={newRegion}
                        onChange={(e) => {
                            setNewRegion(e.target.value);
                            setFormErrors((p) => ({...p, region: undefined}));
                        }}
                        error={!!formErrors.region}
                        helperText={formErrors.region}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newIsActive}
                                onChange={(e) => setNewIsActive(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={t('common.status.active')}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={async () => {
                        if (!validateForm()) return;
                        try {
                            const response = await fetch(`${API_BASE_URL}/api/branches/add`, {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({
                                    name: newName,
                                    city: newCity,
                                    code: newCode,
                                    region: newRegion,
                                    active: newIsActive,
                                }),
                            });
                            if (!response.ok) throw new Error(`Failed to add branch: ${response.statusText}`);
                            onClose();
                            setSuccessMessageText(t('hr.branches.success.created'));
                            setShowSuccessMessage(true);
                            await fetchBranches();
                        } catch (err) {
                            setError(err instanceof Error ? err.message : t('hr.branches.error.addFailed'));
                        }
                    }}
                >
                    {t('common.actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
